
// Document detection for the Snapshot capture editor: finds the quadrilateral of
// a page held in front of the camera and rectifies it to a flat image.
//
// This is deliberately dependency-free — the app has no OpenCV/WASM build step,
// and the shapes we care about (one bright sheet against a darker background)
// don't need contour tracing. The pipeline is:
//
//   grayscale -> blur -> Otsu threshold -> largest bright blob ->
//   convex hull -> reduce hull to 4 corners -> sanity checks
//
// and `cropToQuad` then inverse-maps the detected quad through a projective
// transform, so a page shot at an angle comes out square rather than trapezoidal.

// Detection runs on a downscaled copy: a page edge is many pixels wide even at
// 320px, and the flood fill below is O(pixels) per frame of the live preview.
const WORK_WIDTH = 320;

// A blob smaller than this is noise (a highlight, a reflection); one larger is
// almost always the whole frame — an evenly lit wall, or the page pressed right
// up against the lens — neither of which is a page we can crop to.
const MIN_AREA_FRACTION = 0.08;
const MAX_AREA_FRACTION = 0.97;

// How much of the blob the fitted quad has to account for. A page fills its own
// quadrilateral almost completely; a hand, a lamp or a stack of paper doesn't.
const MIN_QUAD_FILL = 0.75;

// Shortest allowed side, in work-resolution pixels. Rejects slivers that pass
// the area test by being long and thin.
const MIN_SIDE = 20;

// Reused across frames of the live preview so a running detection loop doesn't
// allocate a canvas 6 times a second.
let workCanvas = null;

function workContext(width, height) {
    if (!workCanvas) {
        workCanvas = document.createElement('canvas');
    }
    if (workCanvas.width !== width || workCanvas.height !== height) {
        workCanvas.width = width;
        workCanvas.height = height;
    }
    // The whole point of this canvas is readback; without the hint browsers
    // keep it GPU-side and every getImageData stalls on a texture download.
    return workCanvas.getContext('2d', { willReadFrequently: true });
}

function grayscaleOf(pixels, count) {
    const gray = new Uint8ClampedArray(count);
    for (let i = 0; i < count; i++) {
        const p = i * 4;
        gray[i] = (pixels[p] * 77 + pixels[p + 1] * 151 + pixels[p + 2] * 28) >> 8;
    }
    return gray;
}

// Separable 3x3 box blur. Smooths out paper texture and sensor noise so the
// threshold produces one solid blob instead of a speckled one.
function blur(gray, width, height) {
    const temp = new Uint8ClampedArray(gray.length);

    for (let y = 0; y < height; y++) {
        const row = y * width;
        for (let x = 0; x < width; x++) {
            const left = x > 0 ? gray[row + x - 1] : gray[row + x];
            const right = x < width - 1 ? gray[row + x + 1] : gray[row + x];
            temp[row + x] = (left + gray[row + x] + right) / 3;
        }
    }

    for (let y = 0; y < height; y++) {
        const row = y * width;
        const up = y > 0 ? row - width : row;
        const down = y < height - 1 ? row + width : row;
        for (let x = 0; x < width; x++) {
            gray[row + x] = (temp[up + x] + temp[row + x] + temp[down + x]) / 3;
        }
    }
}

// Otsu's method: the threshold that best splits the histogram into two groups.
// It adapts to the room's lighting, which a fixed cutoff can't do.
function otsuThreshold(gray) {
    const histogram = new Int32Array(256);
    for (let i = 0; i < gray.length; i++) {
        histogram[gray[i]]++;
    }

    let sum = 0;
    for (let t = 0; t < 256; t++) {
        sum += t * histogram[t];
    }

    let sumBackground = 0;
    let weightBackground = 0;
    let bestVariance = -1;
    let threshold = 127;

    for (let t = 0; t < 256; t++) {
        weightBackground += histogram[t];
        if (weightBackground === 0) continue;

        const weightForeground = gray.length - weightBackground;
        if (weightForeground === 0) break;

        sumBackground += t * histogram[t];
        const meanBackground = sumBackground / weightBackground;
        const meanForeground = (sum - sumBackground) / weightForeground;
        const between = weightBackground * weightForeground *
            (meanBackground - meanForeground) * (meanBackground - meanForeground);

        if (between > bestVariance) {
            bestVariance = between;
            threshold = t;
        }
    }

    return threshold;
}

// Iterative 4-connected flood fill over every bright pixel, keeping the biggest
// region found. Iterative rather than recursive because a full-frame region
// would blow the call stack.
function largestBrightRegion(gray, threshold, width, height) {
    const count = width * height;
    const labels = new Int32Array(count); // 0 = unvisited/dark
    const stack = new Int32Array(count);

    let label = 0;
    let bestLabel = 0;
    let bestArea = 0;

    for (let seed = 0; seed < count; seed++) {
        if (labels[seed] !== 0 || gray[seed] <= threshold) continue;

        label++;
        let area = 0;
        let top = 0;
        stack[top++] = seed;
        labels[seed] = label;

        while (top > 0) {
            const index = stack[--top];
            area++;

            const x = index % width;
            const y = (index - x) / width;

            if (x > 0 && labels[index - 1] === 0 && gray[index - 1] > threshold) {
                labels[index - 1] = label;
                stack[top++] = index - 1;
            }
            if (x < width - 1 && labels[index + 1] === 0 && gray[index + 1] > threshold) {
                labels[index + 1] = label;
                stack[top++] = index + 1;
            }
            if (y > 0 && labels[index - width] === 0 && gray[index - width] > threshold) {
                labels[index - width] = label;
                stack[top++] = index - width;
            }
            if (y < height - 1 && labels[index + width] === 0 && gray[index + width] > threshold) {
                labels[index + width] = label;
                stack[top++] = index + width;
            }
        }

        if (area > bestArea) {
            bestArea = area;
            bestLabel = label;
        }
    }

    return { labels, label: bestLabel, area: bestArea };
}

// Every vertex of a convex hull is the leftmost or rightmost pixel of its row,
// so scanning row extremes gives the same hull from a few hundred points
// instead of every pixel in the region.
function rowExtremesOf(region, width, height) {
    const points = [];

    for (let y = 0; y < height; y++) {
        const row = y * width;
        let min = -1;
        let max = -1;
        for (let x = 0; x < width; x++) {
            if (region.labels[row + x] === region.label) {
                if (min < 0) min = x;
                max = x;
            }
        }
        if (min >= 0) {
            points.push([min, y]);
            if (max !== min) points.push([max, y]);
        }
    }

    return points;
}

function cross(o, a, b) {
    return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

// Andrew's monotone chain.
function convexHull(points) {
    if (points.length < 3) return points.slice();

    const sorted = points.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));

    const lower = [];
    for (let i = 0; i < sorted.length; i++) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], sorted[i]) <= 0) {
            lower.pop();
        }
        lower.push(sorted[i]);
    }

    const upper = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], sorted[i]) <= 0) {
            upper.pop();
        }
        upper.push(sorted[i]);
    }

    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

function polygonArea(polygon) {
    let area = 0;
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        area += a[0] * b[1] - b[0] * a[1];
    }
    return Math.abs(area) / 2;
}

// Collapses a hull down to 4 corners by repeatedly dropping the vertex that
// costs the least area — a page's real corners survive because cutting one off
// loses far more area than shaving a rounded edge does.
function reduceToQuad(hull) {
    const polygon = hull.slice();

    while (polygon.length > 4) {
        let victim = 0;
        let smallest = Infinity;

        for (let i = 0; i < polygon.length; i++) {
            const previous = polygon[(i - 1 + polygon.length) % polygon.length];
            const next = polygon[(i + 1) % polygon.length];
            const lost = Math.abs(cross(previous, polygon[i], next)) / 2;
            if (lost < smallest) {
                smallest = lost;
                victim = i;
            }
        }

        polygon.splice(victim, 1);
    }

    return polygon;
}

// Corners come back as [top-left, top-right, bottom-right, bottom-left]. Sorting
// by angle around the centroid walks the quad clockwise on screen (y grows
// downward), and starting from the corner nearest the origin fixes which one is
// "top-left" — so callers can rely on the order regardless of hull winding.
function orderCorners(quad) {
    const centerX = (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4;
    const centerY = (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4;

    const clockwise = quad.slice().sort((a, b) =>
        Math.atan2(a[1] - centerY, a[0] - centerX) - Math.atan2(b[1] - centerY, b[0] - centerX));

    let start = 0;
    let smallest = Infinity;
    for (let i = 0; i < 4; i++) {
        const distance = clockwise[i][0] + clockwise[i][1];
        if (distance < smallest) {
            smallest = distance;
            start = i;
        }
    }

    return [
        clockwise[start],
        clockwise[(start + 1) % 4],
        clockwise[(start + 2) % 4],
        clockwise[(start + 3) % 4]
    ];
}

function distance(a, b) {
    return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/**
 * Looks for a page in `source` (a video, image or canvas).
 *
 * Returns its four corners in *source* pixel coordinates, ordered
 * [top-left, top-right, bottom-right, bottom-left], or null when nothing
 * page-shaped is in view.
 */
export default function detectPageQuad(source, sourceWidth, sourceHeight) {
    if (!source || !sourceWidth || !sourceHeight) return null;

    const width = Math.min(WORK_WIDTH, sourceWidth);
    const height = Math.max(1, Math.round(sourceHeight * (width / sourceWidth)));
    if (width < 8 || height < 8) return null;

    let pixels;
    try {
        const context = workContext(width, height);
        context.drawImage(source, 0, 0, width, height);
        pixels = context.getImageData(0, 0, width, height).data;
    } catch (error) {
        // A cross-origin image taints the canvas and makes it unreadable; there
        // is nothing to detect against, so fall back to no detection.
        return null;
    }

    const gray = grayscaleOf(pixels, width * height);
    blur(gray, width, height);

    const region = largestBrightRegion(gray, otsuThreshold(gray), width, height);
    if (!region.label) return null;

    const frameArea = width * height;
    if (region.area < frameArea * MIN_AREA_FRACTION) return null;
    if (region.area > frameArea * MAX_AREA_FRACTION) return null;

    const hull = convexHull(rowExtremesOf(region, width, height));
    if (hull.length < 4) return null;

    const quad = reduceToQuad(hull);
    if (quad.length !== 4) return null;

    const quadArea = polygonArea(quad);
    if (quadArea < frameArea * MIN_AREA_FRACTION) return null;
    if (quadArea < region.area * MIN_QUAD_FILL) return null;

    const corners = orderCorners(quad);
    for (let i = 0; i < 4; i++) {
        if (distance(corners[i], corners[(i + 1) % 4]) < MIN_SIDE) return null;
    }

    const scale = sourceWidth / width;
    return corners.map((corner) => [corner[0] * scale, corner[1] * scale]);
}

// Projective transform taking the unit square to `quad` (Heckbert's derivation).
// We invert-map the output, so this is exactly the direction we need: for each
// destination pixel, where in the camera frame did it come from.
function unitSquareToQuad(quad) {
    const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;

    const dx1 = x1 - x2;
    const dx2 = x3 - x2;
    const dx3 = x0 - x1 + x2 - x3;
    const dy1 = y1 - y2;
    const dy2 = y3 - y2;
    const dy3 = y0 - y1 + y2 - y3;

    if (dx3 === 0 && dy3 === 0) {
        // The quad is a parallelogram — the transform is affine, no perspective
        // divide, and the general formula below would divide by zero.
        return { a: x1 - x0, b: x2 - x1, c: x0, d: y1 - y0, e: y2 - y1, f: y0, g: 0, h: 0 };
    }

    const denominator = dx1 * dy2 - dx2 * dy1;
    if (denominator === 0) return null;

    const g = (dx3 * dy2 - dx2 * dy3) / denominator;
    const h = (dx1 * dy3 - dx3 * dy1) / denominator;

    return {
        a: x1 - x0 + g * x1,
        b: x3 - x0 + h * x3,
        c: x0,
        d: y1 - y0 + g * y1,
        e: y3 - y0 + h * y3,
        f: y0,
        g,
        h
    };
}

/**
 * Rectifies the region of `source` bounded by `quad` into a new canvas: the
 * page comes out flat and rectangular even when it was photographed at an
 * angle. `maxSize` caps the longer output edge.
 */
export function cropToQuad(source, sourceWidth, sourceHeight, quad, maxSize) {
    if (!quad || quad.length !== 4) return null;

    const transform = unitSquareToQuad(quad);
    if (!transform) return null;

    // Opposite edges disagree under perspective; the longer one is the edge
    // closer to the camera, and using it avoids throwing away resolution.
    let width = Math.max(distance(quad[0], quad[1]), distance(quad[3], quad[2]));
    let height = Math.max(distance(quad[0], quad[3]), distance(quad[1], quad[2]));
    if (!width || !height) return null;

    const limit = maxSize || Math.max(sourceWidth, sourceHeight);
    const shrink = Math.min(1, limit / Math.max(width, height));
    width = Math.max(1, Math.round(width * shrink));
    height = Math.max(1, Math.round(height * shrink));

    let sourcePixels;
    try {
        const reader = document.createElement('canvas');
        reader.width = sourceWidth;
        reader.height = sourceHeight;
        const context = reader.getContext('2d', { willReadFrequently: true });
        context.drawImage(source, 0, 0, sourceWidth, sourceHeight);
        sourcePixels = context.getImageData(0, 0, sourceWidth, sourceHeight).data;
    } catch (error) {
        return null;
    }

    const output = document.createElement('canvas');
    output.width = width;
    output.height = height;
    const outputContext = output.getContext('2d');
    const outputImage = outputContext.createImageData(width, height);
    const target = outputImage.data;

    const { a, b, c, d, e, f, g, h } = transform;
    const maxX = sourceWidth - 1;
    const maxY = sourceHeight - 1;

    for (let py = 0; py < height; py++) {
        const v = (py + 0.5) / height;
        for (let px = 0; px < width; px++) {
            const u = (px + 0.5) / width;

            const w = g * u + h * v + 1;
            const sx = (a * u + b * v + c) / w;
            const sy = (d * u + e * v + f) / w;

            // Bilinear sample, clamped at the edges so a quad that runs off the
            // frame produces edge pixels rather than transparent ones.
            const x0 = Math.min(maxX, Math.max(0, Math.floor(sx)));
            const y0 = Math.min(maxY, Math.max(0, Math.floor(sy)));
            const x1 = Math.min(maxX, x0 + 1);
            const y1 = Math.min(maxY, y0 + 1);
            const fx = Math.min(1, Math.max(0, sx - x0));
            const fy = Math.min(1, Math.max(0, sy - y0));

            const topLeft = (y0 * sourceWidth + x0) * 4;
            const topRight = (y0 * sourceWidth + x1) * 4;
            const bottomLeft = (y1 * sourceWidth + x0) * 4;
            const bottomRight = (y1 * sourceWidth + x1) * 4;

            const out = (py * width + px) * 4;
            for (let channel = 0; channel < 3; channel++) {
                const top = sourcePixels[topLeft + channel] +
                    (sourcePixels[topRight + channel] - sourcePixels[topLeft + channel]) * fx;
                const bottom = sourcePixels[bottomLeft + channel] +
                    (sourcePixels[bottomRight + channel] - sourcePixels[bottomLeft + channel]) * fx;
                target[out + channel] = top + (bottom - top) * fy;
            }
            target[out + 3] = 255;
        }
    }

    outputContext.putImageData(outputImage, 0, 0);
    return output;
}
